import React, { createContext, useContext, useEffect, useMemo } from 'react';
import {
  RaffleTicketsFilter,
  TRaffleDrawItem,
  TRaffleResultItem,
  TUserTicketItem,
} from 'pages/Raffle/types/common.types';
import {
  DEFAULT_USER_TICKETS_FILTER,
  TICKET_FILTERS,
} from 'pages/Raffle/constants';
import useGetDraws from 'pages/Raffle/hooks/useGetDraws/useGetDraws.hooks';
import { Loading } from 'components/Loading';
import useGetUserTickets from 'pages/Raffle/hooks/useGetUserTickets/useGetUserTickets.hooks';
import { resetScroll } from 'utils/scroll';
import useGetRaffleResults from 'pages/Raffle/hooks/useGetRaffleResults/useGetRaffleResults.hooks';
import { getUrlParams } from 'utils/urlParams';
import useGetWinnerItemsCount from 'pages/Raffle/hooks/useGetWinnerItemsCount/useGetWinnerItemsCount.hooks';
import useGetWinnerItems from 'pages/Raffle/hooks/useGetWinnerItems/useGetWinnerItems.hooks';
import { TWinnerItem } from 'types/raffle';

import {
  RAFFLE_RESULTS_PAGE_SIZE,
  USER_TICKETS_PAGE_SIZE,
} from './RaffleTabsProvider.constants';

type TRaffleTabsContext = {
  drawItems: TRaffleDrawItem[] | undefined;
  userTickets: TUserTicketItem[] | undefined;
  getNextUserTickets: () => void;
  error:
    | false
    | {
        message: string;
        errorCode?: number;
      };
  noItem:
    | false
    | {
        mainText: string;
        descriptionText: string;
      };
  refetchItems: () => void;
  isLoadingData: boolean;
  winnerItemsCount: number;
  hasErrorGettingWinnerItemsCount: boolean;
  getNextWinningItems: () => void;
  winningItems: TWinnerItem[] | undefined;
  raffleResults: {
    data: TRaffleResultItem[] | undefined;
    getNextPage: () => void;
    hasNextPage?: boolean;
  };
};

type RaffleTabsProviderItem = {
  getItems: () => void;
  refetchItems: () => void;
  items:
    | TRaffleDrawItem[]
    | TUserTicketItem[]
    | TRaffleResultItem[]
    | TWinnerItem[]
    | undefined;
  hasError: boolean;
  noItemMainText: string;
  noItemDescriptionText: string;
  errorMessage?: string;
};

export type raffleTabs =
  | 'raffles'
  | 'raffle-tickets'
  | 'raffle-results'
  | 'raffle-winnings';

export const RaffleTabsContext = createContext<TRaffleTabsContext>(
  {} as TRaffleTabsContext
);
export const RaffleTabsProvider: React.FC = ({ children }) => {
  const raffleTicketsFilter: RaffleTicketsFilter = useMemo(() => {
    return (
      (getUrlParams().get('raffleTicketsFilter') as RaffleTicketsFilter) ||
      DEFAULT_USER_TICKETS_FILTER
    );
  }, [window.location.search]);

  const {
    drawItems,
    isQueryingGetDraws,
    getDrawItems,
    hasErrorQueryingDraws,
    stopRefetchInterval,
    errorMessage: getDrawsErrorMessage,
    errorCode,
  } = useGetDraws();

  const {
    isQueryingGetTickets,
    getUserRaffleTickets,
    userTickets,
    hasErrorQueryingTickets,
    getTicketsErrorMessage,
    refetchTicketItems,
    isFetchingNextPage: isFetchingTicketsNextPage,
  } = useGetUserTickets({
    filter: raffleTicketsFilter,
    size: USER_TICKETS_PAGE_SIZE,
  });

  const {
    winnerItemsCount,
    handleGetWinnerItemsCount,
    hasErrorGettingWinnerItemsCount,
  } = useGetWinnerItemsCount();

  const {
    winningItems,
    handleGetWinnerItems,
    hasErrorGettingWinnerItems,
    errorMessage: winningItemsErrorMessage,
    refetchItems: refetchWinningItems,
  } = useGetWinnerItems();

  const {
    isQueryingRaffleResults,
    handleGetCompletedRaffleResults,
    hasErrorQueryingRaffleResults,
    raffleResults,
    hasMoreRaffleResults,
  } = useGetRaffleResults({ pageSize: RAFFLE_RESULTS_PAGE_SIZE });

  const urlParam = useMemo(() => {
    const param = (getUrlParams().get('tab') ||
      window.location.pathname.split('/')[1]) as raffleTabs;
    if (param !== 'raffles') {
      stopRefetchInterval();
    }
    return param;
  }, [window.location.pathname]);

  function getAllWinningItems() {
    handleGetWinnerItemsCount();
    handleGetWinnerItems();
  }

  const raffleTab: {
    [key in raffleTabs]: RaffleTabsProviderItem;
  } = {
    raffles: {
      getItems: getDrawItems,
      items: drawItems,
      hasError: hasErrorQueryingDraws,
      noItemMainText: 'Aktif çekiliş yok.',
      noItemDescriptionText: 'Şuan mevcut aktif bir çekiliş bulunamadı.',
      errorMessage: getDrawsErrorMessage,
      refetchItems: getDrawItems,
    },
    'raffle-tickets': {
      getItems: getUserRaffleTickets,
      items: userTickets,
      hasError: hasErrorQueryingTickets,
      noItemMainText: TICKET_FILTERS[raffleTicketsFilter].noFilteredItemText,
      noItemDescriptionText:
        TICKET_FILTERS[raffleTicketsFilter].noFilteredItemDescriptionText,
      errorMessage: getTicketsErrorMessage,
      refetchItems: refetchTicketItems,
    },
    'raffle-results': {
      getItems: handleGetCompletedRaffleResults,
      items: raffleResults,
      hasError: hasErrorQueryingRaffleResults,
      noItemMainText: 'Sonuçlanan çekiliş yok.',
      noItemDescriptionText:
        'Mevcut çekilişler sonuçlandığında buradan görüntüleyebilirsiniz.',
      refetchItems: handleGetCompletedRaffleResults,
    },
    'raffle-winnings': {
      getItems: getAllWinningItems,
      hasError: hasErrorGettingWinnerItems,
      items: winningItems,
      noItemDescriptionText: '',
      noItemMainText: '',
      errorMessage: winningItemsErrorMessage,
      refetchItems: refetchWinningItems,
    },
  };

  function shouldCancelGetItems() {
    return (
      (urlParam === 'raffle-results' || urlParam === 'raffle-winnings') &&
      raffleTab[urlParam].items &&
      raffleTab[urlParam].items?.length
    );
  }

  useEffect(() => {
    const tabElement = document.getElementById('tab-content');

    if (tabElement) {
      resetScroll({ element: tabElement });
    }
    if (shouldCancelGetItems()) {
      return;
    }
    raffleTab[urlParam].getItems();
  }, [urlParam, raffleTicketsFilter]);

  const isLoadingData =
    isQueryingGetDraws ||
    isQueryingGetTickets ||
    isQueryingRaffleResults ||
    isQueryingGetTickets ||
    isFetchingTicketsNextPage;

  const item = {
    drawItems,
    userTickets: raffleTab['raffle-tickets'].items as
      | TUserTicketItem[]
      | undefined,
    getNextUserTickets: getUserRaffleTickets,
    raffleResults: {
      data: raffleResults,
      getNextPage: handleGetCompletedRaffleResults,
      hasNextPage: hasMoreRaffleResults,
    },
    error: raffleTab[urlParam].hasError && {
      message: raffleTab[urlParam].errorMessage ?? 'Bir hata meydana geldi.',
      errorCode,
    },
    noItem: !raffleTab[urlParam].items?.length && {
      mainText: raffleTab[urlParam].noItemMainText,
      descriptionText: raffleTab[urlParam].noItemDescriptionText,
    },
    refetchItems: raffleTab[urlParam].refetchItems,
    isLoadingData,
    winnerItemsCount,
    hasErrorGettingWinnerItemsCount,
    getNextWinningItems: handleGetWinnerItems,
    winningItems,
  };

  return (
    <RaffleTabsContext.Provider value={item}>
      {isLoadingData && <Loading tabLoading />}
      {children}
    </RaffleTabsContext.Provider>
  );
};

export function useRaffleTabs(): TRaffleTabsContext {
  return useContext(RaffleTabsContext);
}
